<template>
  <article
    class="home-editorial-card home-card-frame"
    :class="{ 'home-editorial-card--original': usesOriginalStyle }"
  >
    <span v-if="usesOriginalStyle" class="home-editorial-card__status">{{ statusLabel }}</span>
    <img class="home-editorial-card__image" :src="project.project_banner" :alt="project.name" loading="lazy" @error="onImageError" />
    <span class="home-editorial-card__veil" aria-hidden="true"></span>
    <span
      v-if="project.ageRatingLabel"
      class="home-age-rating"
      :class="project.ageRatingClass"
      :aria-label="`Classificacao indicativa ${project.ageRatingLabel}`"
    >
      {{ project.ageRatingLabel }}
    </span>
    <span class="home-editorial-card__content">
      <span v-if="label" class="home-editorial-card__label">{{ label }}</span>
      <strong>{{ project.name }}</strong>
      <span v-if="usesOriginalStyle" class="home-editorial-card__meta">{{ metaLabel }}</span>
      <small>{{ project.description }}</small>
      <span v-if="usesOriginalStyle" class="home-editorial-card__actions">
        <router-link class="home-editorial-card__cta" :to="project.href">
          Iniciar leitura
        </router-link>
        <button
          type="button"
          class="home-editorial-card__bookmark"
          :class="{ 'home-editorial-card__bookmark--saved': isFavorite }"
          :aria-label="favoriteButtonLabel"
          :aria-pressed="String(isFavorite)"
          :disabled="isSaving"
          :title="favoriteButtonLabel"
          @click="toggleFavorite"
        >
          <v-progress-circular v-if="isSaving" indeterminate :size="18" :width="2" />
          <v-icon v-else>{{ isFavorite ? "mdi-bookmark" : "mdi-bookmark-outline" }}</v-icon>
        </button>
      </span>
      <span class="home-editorial-card__feedback" role="status" aria-live="polite">
        {{ feedbackMessage }}
      </span>
    </span>
  </article>
</template>

<script>
import { HOME_BANNER_FALLBACK, setImageFallback } from "@/utils/homeImageFallbacks";
import { FavoriteProjectService } from "@/server/FavoriteService";

export default {
  name: "HomeEditorialProjectCard",
  props: {
    project: { type: Object, required: true },
    label: { type: String, default: "" },
  },
  data() {
    return {
      isFavorite: Boolean(this.project.is_favorite),
      isSaving: false,
      feedbackMessage: "",
    };
  },
  computed: {
    usesOriginalStyle() {
      return ["Original", "Exclusivo"].includes(this.label);
    },
    statusLabel() {
      if (this.project.completed) return "Concluido";
      return "Em andamento";
    },
    metaLabel() {
      const parts = [
        this.project.project_type?.name || this.project.type?.name || this.project.type_reading || "Quadrinho",
        this.project.category?.name || this.project.project_category?.name || "Serie",
        this.project.gender?.name || this.project.genre || "Aventura",
      ];

      return parts.filter(Boolean).join(" / ");
    },
    favoriteButtonLabel() {
      return this.isFavorite ? "Remover obra dos salvos" : "Salvar obra";
    },
  },
  watch: {
    "project.is_favorite"(isFavorite) {
      this.isFavorite = Boolean(isFavorite);
    },
  },
  methods: {
    onImageError(event) {
      setImageFallback(event, HOME_BANNER_FALLBACK);
    },
    async toggleFavorite() {
      if (this.isSaving) return;

      if (!localStorage.getItem("auth")) {
        this.feedbackMessage = "Entre na sua conta para salvar esta obra.";
        this.$router.push({ name: "Login" });
        return;
      }

      this.isSaving = true;
      this.feedbackMessage = "";

      try {
        const service = new FavoriteProjectService();
        const response = await service.store({ project_id: this.project.id });
        const nextFavoriteState = response?.is_follow;

        this.isFavorite = typeof nextFavoriteState === "boolean"
          ? nextFavoriteState
          : !this.isFavorite;
        this.feedbackMessage = this.isFavorite
          ? "Obra salva com sucesso."
          : "Obra removida dos salvos.";
        this.$emit("favorite-changed", {
          projectId: this.project.id,
          isFavorite: this.isFavorite,
        });
      } catch (error) {
        console.error("Erro ao atualizar obra salva:", error);
        this.feedbackMessage = "Não foi possível atualizar a obra salva. Tente novamente.";
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
